import React, { useEffect, useState } from "react";
import "./ProjectCoverImage.scss";
import axios from "axios";
import classNames from "classnames";
import { useSelector } from "react-redux";

import { TaskType } from "src/types";
import { RootState } from "src/redux/reducers";
import { ReactComponent as PlusIcon } from "../../../images/plus-transparent.svg";
import { ReactComponent as SettingsIcon } from "../../../images/settings-v.svg";
import PlaceholderImage from "../../../images/project-square-placeholder.svg";
import publicationPlaceholder from "../../../images/publication-placeholder.svg";
import { API_URLS } from "../../../utils/API_URLS";
import { Dropdown, DropdownMenu, DropdownMenuItem } from "../Dropdown/Dropdown";
import { showToast } from "../../methods/showToast";
import Skeleton from "../Skeleton/Skeleton";
import CustomImage from "../CustomImage/CustomImage";
import { getImageSource } from "../../../utils/methods";
import ImageCropModal from "../CroppedImageUploader/ImageCropModal/ImageCropModal";
import IDHFormattedMessage from "../IDHFormattedMessage/IDHFormattedMessage";

type Props = {
  item: any;
  src?: string | null;
  something?: string;
  updateData?: () => void;
  parentLoading?: boolean;
};

function ProjectCoverImage(props: Props) {
  // this all file is copied from PersonProfilePicture component. Kacper TODO: create one component for this and use uploadFile class
  const { src, item, updateData, parentLoading } = props;

  const { activeWorkspaceUuid } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const [coverImageUrl, setCoverImageUrl] = useState<null | string>(
    src || null,
  );
  const [coverImageId, setCoverImageId] = useState(item.cover?.id);

  const [coverImageFile, setCoverImageFile] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [showUploadLogoModal, setShowUploadLogoModal] =
    useState<boolean>(false);

  useEffect(() => {
    setCoverImageUrl(src || null);
  }, [src]);

  const setNewImage = (file: any) => {
    if (!file) {
      return;
    }

    let fileExtension = file.name.split(".");
    fileExtension = fileExtension[fileExtension.length - 1];
    const allowedTypes = ["png", "jpg", "jpeg"];

    if (!file) {
      return;
    }

    if (!allowedTypes.includes(fileExtension.toLowerCase())) {
      showToast("error", "Error", "Invalid file type.");
      return;
    }

    const size = file.size / parseInt((1000 * 1000).toFixed(2), 10);
    if (size > 10) {
      showToast("error", "Error", "File is too large.");
    } else {
      setCoverImageFile(file);
      setLoading(true);
    }
  };

  useEffect(() => {
    if (coverImageFile) {
      uploadMedia();
    }
  }, [coverImageFile]);

  // useEffect(() => {
  //   setCoverImageUrl(src);
  // }, [src]);

  useEffect(() => {
    const src = getImageSource(item?.cover, "small");
    if (src && src !== coverImageUrl) {
      setCoverImageUrl(src);
    }
  }, [item?.cover?.thumbnails]);

  const uploadMedia = () => {
    // const wsTaskUuid = item.projectId || item.taskId;
    const wsTaskUuid = item.projectId; // copied from PersonProfilePicture

    setLoading(true);

    setCoverImageUrl(null);

    axios
      .get(
        `${API_URLS.presignedRequestForProjectCover}?wsProjectUuid=${wsTaskUuid}&wsWorkspaceUuid=${activeWorkspaceUuid}`,
      )
      .then(function (response) {
        const {
          requestInputs,
          requestAttributes: { action },
        } = response.data.content;

        axios
          .post(action, getFormDataToUploadS3(coverImageFile, requestInputs))
          .then(function (response) {
            if (response.status === 201) {
              const parentUuid = item.projectId;
              const requestInputsKeyArray = requestInputs.key.split("/");
              const realName =
                requestInputsKeyArray[requestInputsKeyArray.length - 1];

              axios
                .post(API_URLS.createAsset, {
                  wsWorkspaceUuid: activeWorkspaceUuid,
                  parentUuid,
                  parentType: "ws_project",
                  name: coverImageFile.name,
                  realName,
                  mimeType: coverImageFile.type,
                  size: coverImageFile.size,
                })
                .then(function (response) {
                  if (updateData) {
                    updateData();
                  }
                  setLoading(false);
                  setCoverImageUrl(response.data.content.url);
                  setCoverImageId(response.data.content.id);
                });
            }
          })
          .catch(function (error) {
            console.error(error.response?.data?.content?.errors);
            setLoading(false);
          });
      })
      .catch(function (error) {
        setLoading(false);
      });
  };

  const getFormDataToUploadS3 = (file: File, requestInputs: any) => {
    const formData = new FormData();

    formData.set("key", requestInputs.key);
    formData.set("acl", requestInputs.acl);
    formData.set("X-Amz-Credential", requestInputs["X-Amz-Credential"]);
    formData.set("X-Amz-Algorithm", requestInputs["X-Amz-Algorithm"]);
    formData.set("X-Amz-Date", requestInputs["X-Amz-Date"]);
    formData.set("Policy", requestInputs.Policy);
    formData.set("X-Amz-Signature", requestInputs["X-Amz-Signature"]);
    formData.set("success_action_status", requestInputs.success_action_status);
    formData.set("Content-Type", file.type);
    formData.set("file", file);

    return formData;
  };

  const removeCoverPhoto = () => {
    axios
      .delete(`${API_URLS.deleteAsset}/${activeWorkspaceUuid}/${coverImageId}`)
      .then(function () {
        setCoverImageUrl(null);

        if (updateData) {
          updateData();
        }
      });
  };

  return (
    <div
      className={classNames("person-profile-picture", {
        "person-profile-picture--square": true,
      })}
    >
      {loading || parentLoading ? (
        <Skeleton
          width={80}
          height={80}
          borderRadius={item.taskType === TaskType.Content ? "4px" : "50%"}
        />
      ) : (
        <CustomImage
          className="person-profile-picture__photo"
          src={
            coverImageUrl ||
            (item.taskType === TaskType.Publication
              ? publicationPlaceholder
              : PlaceholderImage)
          }
          fallbackImageSrc={
            item.taskType === TaskType.Publication
              ? publicationPlaceholder
              : PlaceholderImage
          }
          alt="profile-pic-example"
          loading="lazy"
        />
      )}

      {!coverImageUrl && !loading && !parentLoading ? (
        <div
          className="person-profile-picture__button"
          onClick={() => setShowUploadLogoModal(true)}
        >
          <PlusIcon />
        </div>
      ) : !loading && !parentLoading ? (
        <Dropdown>
          {(isOpen, setIsOpen) => (
            <>
              <div
                className="person-profile-picture__button"
                onClick={() => setIsOpen((prev) => !prev)}
              >
                <SettingsIcon />
              </div>

              <DropdownMenu isOpen={isOpen}>
                <DropdownMenuItem onClick={removeCoverPhoto}>
                  <IDHFormattedMessage
                    id="ws_remove_cover_photo"
                    defaultMessage="Remove cover photo"
                  />
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="image-input"
                  onClick={() => setShowUploadLogoModal(true)}
                >
                  <IDHFormattedMessage
                    id="ws_change_cover_photo"
                    defaultMessage="Change cover photo"
                  />
                </DropdownMenuItem>
              </DropdownMenu>
            </>
          )}
        </Dropdown>
      ) : null}
      {showUploadLogoModal && (
        <ImageCropModal
          uploadFunction={setNewImage}
          onClose={() => setShowUploadLogoModal(false)}
        />
      )}
    </div>
  );
}

export default ProjectCoverImage;
